/**
 * W6 vertical-section view — TVD (down) vs. horizontal displacement (right),
 * the canonical "side profile" of a directional well. Dependency-free SVG on
 * the shared ChartFrame (no Plotly, no external refs — CSP-safe). The path is
 * drawn per trajectory segment (vertical / build / lateral) so each reads in its
 * operation colour, and a KOP marker line is dropped at the first station that
 * leaves vertical. Renders nothing when there is no placeable point (all-null
 * TVD / a not-yet-computed draft) — the caller shows an empty state instead.
 */
import { useMemo } from "react";
import { ChartFrame } from "@components/chart/ChartFrame";
import type { SectionPoint, TrajectorySegment } from "./trajectoryModel";

export interface VerticalSectionChartProps {
  points: SectionPoint[];
  /** MD (m) at the kick-off point — dropped as a dashed marker line. */
  kopTvd?: number | null;
  kopLabel?: string;
  xLabel?: string;
  yLabel?: string;
  ariaLabel?: string;
}

/** The `--op-*` token each segment renders in. */
const SEGMENT_VAR: Record<TrajectorySegment, string> = {
  vertical: "--dim",
  build: "--op-unconv",
  lateral: "--op-drilling",
};

/** Round a domain max up to a "nice" step so axis labels read cleanly. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

/** Evenly-spaced axis ticks 0..max (inclusive), `count` intervals. */
function axisTicks(max: number, count: number): number[] {
  const step = max / count;
  return Array.from({ length: count + 1 }, (_, i) => Math.round(i * step * 100) / 100);
}

export function VerticalSectionChart({
  points,
  kopTvd,
  kopLabel,
  xLabel,
  yLabel,
  ariaLabel,
}: VerticalSectionChartProps) {
  const xMax = useMemo(() => niceMax(Math.max(1, ...points.map((p) => p.x))), [points]);
  const yMax = useMemo(() => niceMax(Math.max(1, ...points.map((p) => p.y))), [points]);

  // A degenerate plot (no placeable point) renders nothing — the screen shows
  // its own empty state. A single point still draws (one marker at origin).
  if (points.length === 0) return null;

  return (
    <ChartFrame
      xDomain={[0, xMax]}
      yDomain={[0, yMax]}
      yDown
      width={480}
      height={320}
      xLabel={xLabel}
      yLabel={yLabel}
      ariaLabel={ariaLabel}
    >
      {({ plot, sx, sy }) => {
        const xTicks = axisTicks(xMax, 4);
        const yTicks = axisTicks(yMax, 4);
        // Split the polyline into per-segment strokes: each vertex joins to the
        // NEXT vertex using the segment of the deeper (later) station.
        const strokes = points.slice(1).map((p, i) => {
          const prev = points[i]!;
          return {
            key: `s${prev.seq}-${p.seq}`,
            seg: p.segment,
            line: `${sx(prev.x)},${sy(prev.y)} ${sx(p.x)},${sy(p.y)}`,
          };
        });
        return (
          <g>
            {/* horizontal gridlines + TVD (y) tick labels */}
            {yTicks.map((v) => (
              <g key={`y${v}`}>
                <line
                  x1={plot.x}
                  x2={plot.x + plot.w}
                  y1={sy(v)}
                  y2={sy(v)}
                  stroke="var(--line)"
                  strokeWidth={0.5}
                  opacity={0.55}
                />
                <text
                  x={plot.x - 6}
                  y={sy(v) + 3}
                  textAnchor="end"
                  fontSize={9.5}
                  fontFamily="var(--font-mono)"
                  fill="var(--dim)"
                >
                  {Math.round(v).toLocaleString()}
                </text>
              </g>
            ))}
            {/* vertical gridlines + displacement (x) tick labels */}
            {xTicks.map((v) => (
              <g key={`x${v}`}>
                <line
                  x1={sx(v)}
                  x2={sx(v)}
                  y1={plot.y}
                  y2={plot.y + plot.h}
                  stroke="var(--line)"
                  strokeWidth={0.5}
                  opacity={0.35}
                  strokeDasharray="3,3"
                />
                <text
                  x={sx(v)}
                  y={plot.y + plot.h + 13}
                  textAnchor="middle"
                  fontSize={9}
                  fontFamily="var(--font-mono)"
                  fill="var(--dim)"
                >
                  {Math.round(v).toLocaleString()}
                </text>
              </g>
            ))}
            {/* KOP marker line (only when a kick-off TVD is known + in range) */}
            {kopTvd != null && kopTvd > 0 && kopTvd <= yMax && (
              <g>
                <line
                  x1={plot.x}
                  x2={plot.x + plot.w}
                  y1={sy(kopTvd)}
                  y2={sy(kopTvd)}
                  stroke="var(--warn)"
                  strokeWidth={0.75}
                  strokeDasharray="3,3"
                  opacity={0.6}
                />
                {kopLabel && (
                  <text
                    x={plot.x + plot.w}
                    y={sy(kopTvd) - 3}
                    textAnchor="end"
                    fontSize={9}
                    fontFamily="var(--font-mono)"
                    fill="var(--warn)"
                  >
                    {kopLabel}
                  </text>
                )}
              </g>
            )}
            {/* per-segment trajectory strokes */}
            {strokes.map((s) => (
              <polyline
                key={s.key}
                points={s.line}
                fill="none"
                stroke={`var(${SEGMENT_VAR[s.seg]})`}
                strokeWidth={2.5}
                strokeLinecap="round"
              />
            ))}
            {/* surface + TD station markers */}
            {points.map((p, i) => {
              const isEnd = i === points.length - 1;
              return (
                <circle
                  key={`m${p.seq}`}
                  cx={sx(p.x)}
                  cy={sy(p.y)}
                  r={isEnd ? 3.5 : 2.5}
                  fill={`var(${SEGMENT_VAR[p.segment]})`}
                >
                  <title>
                    {Math.round(p.x).toLocaleString()} m disp · {Math.round(p.y).toLocaleString()} m
                    TVD
                  </title>
                </circle>
              );
            })}
          </g>
        );
      }}
    </ChartFrame>
  );
}
